import React, { useEffect, useState } from 'react';
import { getRecipeByMenuProduct } from '../services/menuProductPortionService';
import { getPortions } from '../services/portionService';

const round2 = (n) => Math.round(n * 100) / 100;

// item: línea del carrito { menuProductId, name, quantity, portions }
// portions (por unidad): null = receta original; si no, [{ portionId, name, unitType, quantity, recipeQuantity }]
export default function ItemPorcionesModal({ item, onClose, onSave }) {
  const [rows, setRows] = useState([]);
  const [allPortions, setAllPortions] = useState([]);
  const [toAdd, setToAdd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([getRecipeByMenuProduct(item.menuProductId), getPortions()])
      .then(([recipe, portions]) => {
        const base = (Array.isArray(recipe) ? recipe : []).map(r => ({
          portionId: r.portionId,
          name: r.portion?.name || `Porción ${r.portionId}`,
          unitType: r.portion?.unitType || '',
          recipeQuantity: Number(r.quantity),
          quantity: Number(r.quantity),
        }));

        // Aplicar la personalización guardada (si la hay)
        if (item.portions) {
          for (const p of item.portions) {
            const row = base.find(b => b.portionId === p.portionId);
            if (row) row.quantity = Number(p.quantity);
            else base.push({ ...p, recipeQuantity: 0, quantity: Number(p.quantity) });
          }
        }

        setRows(base);
        setAllPortions(Array.isArray(portions) ? portions.filter(p => p.state) : []);
      })
      .catch(() => setError('No se pudo cargar la receta del producto.'))
      .finally(() => setLoading(false));
  }, [item]);

  function setQty(portionId, value) {
    const qty = Math.max(0, round2(Number(value) || 0));
    setRows(prev => prev.map(r => r.portionId === portionId ? { ...r, quantity: qty } : r));
  }

  function removeExtra(portionId) {
    setRows(prev => prev.filter(r => r.portionId !== portionId));
  }

  function addPortion() {
    const portion = allPortions.find(p => String(p.id) === String(toAdd));
    if (!portion) return;
    setRows(prev => [...prev, {
      portionId: portion.id, name: portion.name, unitType: portion.unitType || '', recipeQuantity: 0, quantity: 1,
    }]);
    setToAdd('');
  }

  function resetRecipe() {
    setRows(prev => prev.filter(r => r.recipeQuantity > 0).map(r => ({ ...r, quantity: r.recipeQuantity })));
  }

  function handleSave() {
    const effective = rows.filter(r => r.recipeQuantity > 0 || r.quantity > 0);
    const isOriginal = effective.every(r => r.quantity === r.recipeQuantity);
    onSave(isOriginal ? null : effective.map(r => ({
      portionId: r.portionId,
      name: r.name,
      unitType: r.unitType,
      quantity: r.quantity,
      recipeQuantity: r.recipeQuantity,
    })));
  }

  const available = allPortions.filter(p => !rows.some(r => r.portionId === p.id));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 520, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className="bi bi-sliders" style={{ color: 'var(--orig-green)' }} />
            Porciones — {item.name}
          </h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 14 }}>
            Cantidades por unidad.
            {item.quantity > 1 && <> Se aplican a las <strong>{item.quantity} unidades</strong> de esta línea.</>}
          </p>

          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{ fontSize: 24, display: 'block', marginBottom: 8, animation: 'spin 1s linear infinite' }} />
              Cargando receta...
            </div>
          ) : (
            <>
              {rows.length === 0 && (
                <div style={{ padding: '14px 0', fontSize: 12.5, color: 'var(--orig-text-muted)' }}>
                  Este producto no tiene receta. Puede agregar porciones abajo.
                </div>
              )}

              {rows.map(r => {
                const removed = r.recipeQuantity > 0 && r.quantity === 0;
                const isExtra = r.recipeQuantity === 0;
                return (
                  <div
                    key={r.portionId}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 0', borderBottom: '1px solid var(--orig-border)',
                      opacity: removed ? 0.55 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--orig-text)',
                        textDecoration: removed ? 'line-through' : 'none',
                      }}>
                        {r.name}
                        {isExtra && <span className="badge-orig badge-amber" style={{ marginLeft: 8 }}>Adicional</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                        {r.unitType}{!isExtra && ` · receta: ${r.recipeQuantity}`}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button type="button" className="fact-step" onClick={() => setQty(r.portionId, r.quantity - 1)} title="Restar">
                        <i className="bi bi-dash" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        className="form-control"
                        value={r.quantity}
                        onChange={e => setQty(r.portionId, e.target.value)}
                        style={{ width: 58, textAlign: 'center', padding: '4px 2px', marginBottom: 0 }}
                      />
                      <button type="button" className="fact-step" onClick={() => setQty(r.portionId, r.quantity + 1)} title="Sumar">
                        <i className="bi bi-plus" />
                      </button>
                    </div>

                    {isExtra ? (
                      <button
                        type="button"
                        className="btn-orig btn-orig-outline"
                        onClick={() => removeExtra(r.portionId)}
                        style={{ padding: '5px 10px', color: 'var(--orig-red-light)', width: 84, justifyContent: 'center' }}
                        title="Quitar adicional"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-orig btn-orig-outline"
                        onClick={() => setQty(r.portionId, removed ? r.recipeQuantity : 0)}
                        style={{ padding: '5px 8px', fontSize: 11.5, width: 84, justifyContent: 'center', color: removed ? 'var(--orig-green-bright)' : 'var(--orig-red-light)' }}
                      >
                        {removed ? 'Restaurar' : 'Quitar'}
                      </button>
                    )}
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <select
                  className="form-select"
                  value={toAdd}
                  onChange={e => setToAdd(e.target.value)}
                  style={{ marginBottom: 0 }}
                >
                  <option value="">Agregar porción adicional...</option>
                  {available.map(p => (
                    <option key={p.id} value={p.id}>{p.name}{p.unitType ? ` — ${p.unitType}` : ''}</option>
                  ))}
                </select>
                <button type="button" className="btn-orig btn-orig-green" onClick={addPortion} disabled={!toAdd} style={{ flexShrink: 0 }}>
                  <i className="bi bi-plus-lg" />
                  Agregar
                </button>
              </div>
            </>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 10,
          padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
        }}>
          <button type="button" className="btn-orig btn-orig-outline" onClick={resetRecipe} disabled={loading}>
            <i className="bi bi-arrow-counterclockwise" />
            Receta original
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-orig btn-orig-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="btn-orig btn-orig-green" onClick={handleSave} disabled={loading || !!error}>
              <i className="bi bi-check2" />
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
